/**
 * useFavoritePages Hook
 *
 * Provides functionality for favoriting pages:
 * - favoritePages: array of hrefs from user preferences
 * - isFavorite(href): check if a page is favorited
 * - toggleFavorite(href): toggle favorite status (writes to Firestore)
 * - sortWithFavorites(links): reorder links with favorites first
 *
 * Data is stored in: users/{email}.uiPreferences.favoritePages
 * Syncs in real-time via useUserPermissionsListener
 *
 * Note: Favorites are tracked by href (not pageId) to support pages that
 * appear in multiple departments (e.g., /parts/directory vs /rental/directory).
 */

import { useCallback, useMemo } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { useFirebaseStore } from '../store/firebaseStore';
import { logger } from '../utils/logger';
import type { Link } from '../components/Sidebar/sidebar-config';

export interface UseFavoritePagesReturn {
  /** Array of favorited page hrefs */
  favoritePages: string[];
  /** Check if a specific page href is favorited */
  isFavorite: (href: string) => boolean;
  /** Toggle favorite status for a page by href */
  toggleFavorite: (href: string) => Promise<void>;
  /** Sort links array with favorites first, maintaining original order within groups */
  sortWithFavorites: <T extends Pick<Link, 'href'>>(links: T[]) => T[];
}

export function useFavoritePages(): UseFavoritePagesReturn {
  const { currentUser, db } = useFirebaseStore();

  // Get favorite pages from user preferences (synced via useUserPermissionsListener)
  const favoritePages = useMemo(
    () => currentUser?.uiPreferences?.favoritePages ?? [],
    [currentUser?.uiPreferences?.favoritePages]
  );

  // Check if a specific page href is favorited
  const isFavorite = useCallback(
    (href: string): boolean => {
      return favoritePages.includes(href);
    },
    [favoritePages]
  );

  // Toggle favorite status for a page by href
  const toggleFavorite = useCallback(
    async (href: string): Promise<void> => {
      if (!currentUser?.email || !db) {
        logger.warn('[useFavoritePages] Cannot toggle favorite: no user or db');
        return;
      }

      const isCurrentlyFavorited = favoritePages.includes(href);
      const newFavorites = isCurrentlyFavorited
        ? favoritePages.filter((h) => h !== href)
        : [...favoritePages, href];

      try {
        const userRef = doc(db, 'users', currentUser.email);
        logger.firestore('Write', `users/${currentUser.email} (toggle favorite: ${href})`);
        await setDoc(
          userRef,
          {
            uiPreferences: { favoritePages: newFavorites },
          },
          { merge: true }
        );
      } catch (error) {
        logger.error('[useFavoritePages] Failed to toggle favorite:', error);
        throw error;
      }
    },
    [currentUser?.email, db, favoritePages]
  );

  // Sort links with favorites first, maintaining original order within each group
  const sortWithFavorites = useCallback(
    <T extends Pick<Link, 'href'>>(links: T[]): T[] => {
      if (favoritePages.length === 0) return links;

      const favorites: T[] = [];
      const nonFavorites: T[] = [];

      links.forEach((link) => {
        if (favoritePages.includes(link.href)) {
          favorites.push(link);
        } else {
          nonFavorites.push(link);
        }
      });

      return [...favorites, ...nonFavorites];
    },
    [favoritePages]
  );

  return {
    favoritePages,
    isFavorite,
    toggleFavorite,
    sortWithFavorites,
  };
}
